import axios from 'axios';
import { limparSessaoAuth } from './authStorage';

/**
 * Cliente HTTP único do front (padrão P-S2, src/docs/padroes-front.md).
 *
 * Sessão (onda OA, src/docs/plano-adequacao-telas.md): o JWT vive num cookie httpOnly gravado pelo back —
 * o JavaScript da página não o lê nem o guarda. Por isso:
 * - `withCredentials`: o navegador envia o cookie em toda chamada;
 * - gravações (POST/PUT/PATCH/DELETE) levam o cabeçalho X-CSRF-Token, recebido no login/me e mantido
 *   SÓ em memória (some ao recarregar a página; o /me devolve de novo).
 */

/**
 * Endereço da API. REACT_APP_HOST_API vazio ou "auto": mesmo host do front na porta REACT_APP_API_PORT
 * (padrão 8010) — front e API no mesmo "site", condição para o cookie SameSite=Strict ir junto
 * (ex.: localhost:7500 → localhost:8010; 192.168.1.17:7500 → 192.168.1.17:8010).
 */
export function resolverBaseApi(env = process.env, local = typeof window !== 'undefined' ? window.location : null) {
  const configurado = String(env.REACT_APP_HOST_API || '').trim();
  if (configurado && configurado.toLowerCase() !== 'auto') return configurado;
  const porta = env.REACT_APP_API_PORT || '8010';
  if (!local) return `http://localhost:${porta}/`;
  return `${local.protocol}//${local.hostname}:${porta}/`;
}

const api = axios.create({
  baseURL: resolverBaseApi(),
  withCredentials: true,
});

const METODOS_SEGUROS = ['get', 'head', 'options'];
let csrfToken = null;

/** Guarda o token CSRF da sessão (só em memória). */
export function definirCsrfToken(valor) {
  csrfToken = valor || null;
}

export function obterCsrfToken() {
  return csrfToken;
}

/**
 * Sessão que ESTA aba está mostrando (usuário + empresa + filial), só em memória — o localStorage e o cookie são
 * do navegador inteiro. Vai em toda chamada no cabeçalho X-Sessao-Esperada; se outra aba entrou com outro usuário
 * (ou trocou empresa/filial), o back recusa com 409 SESSAO_TROCADA em vez de gravar em nome da sessão nova.
 * Docs: src/docs/sessao-trocada-entre-abas.md (padrão P-S10).
 */
let sessaoEsperada = null;
let onSessaoTrocada = null;

/** @param {null | { usuarioId: any, empresaId: any, filialId: any }} sessao */
export function definirSessaoEsperada(sessao) {
  sessaoEsperada = sessao && sessao.usuarioId != null && sessao.usuarioId !== ''
    ? { usuarioId: String(sessao.usuarioId), empresaId: String(sessao.empresaId ?? ''), filialId: String(sessao.filialId ?? '') }
    : null;
}

export function obterSessaoEsperada() {
  return sessaoEsperada;
}

/** Cabeçalho para chamadas fora do axios (ex.: fetch com keepalive). */
export function cabecalhoSessaoEsperada() {
  if (!sessaoEsperada) return {};
  return { 'X-Sessao-Esperada': `${sessaoEsperada.usuarioId};${sessaoEsperada.empresaId};${sessaoEsperada.filialId}` };
}

/** A sessão do navegador é outra que a mostrada na aba? (compara usuário, empresa e filial) */
export function sessaoDivergeDaAba(sessao) {
  if (!sessaoEsperada || !sessao) return false;
  const usuarioId = sessao.usuarioId ?? sessao.usuario?.id;
  return (
    String(usuarioId ?? '') !== sessaoEsperada.usuarioId ||
    String(sessao.empresaId ?? '') !== sessaoEsperada.empresaId ||
    String(sessao.filialId ?? '') !== sessaoEsperada.filialId
  );
}

/**
 * Registra quem bloqueia a aba quando a sessão foi trocada em outra aba.
 * O handler recebe { sessaoNova?: { usuario, empresaId, filialId } } (sem sessaoNova quando veio só a recusa do back).
 */
export function configurarAvisoSessaoTrocada(handler) {
  onSessaoTrocada = typeof handler === 'function' ? handler : null;
}

export function notificarSessaoTrocada(info = {}) {
  if (typeof onSessaoTrocada === 'function') onSessaoTrocada(info);
}

/** 409 SESSAO_TROCADA (cabeçalho) ou 403 SESSAO_TROCADA (pUsuarioLogado das telas antigas). */
export const ehSessaoTrocada = (erro) => erro?.response?.data?.codigo === 'SESSAO_TROCADA';

/** Callbacks registrados pelo Auth/App (evita dependência circular). */
let onUnauthorized = null;
let onForbidden = null;
let onConcorrencia = null;
let onCadastroIncompleto = null;

/**
 * Registra quem pergunta à pessoa o que fazer quando o back devolve 409 CONCORRENCIA_TENTE_NOVAMENTE
 * (back: docs/integridade-transacional.md, decisão P5). O handler recebe { mensagem, tentarDeNovo, desistir }:
 * `tentarDeNovo()` reenvia o MESMO pedido e entrega a resposta à tela que chamou; `desistir()` devolve o erro a ela.
 * A tela fica esperando a resposta original, então nada do que foi digitado se perde.
 * @param {null | ((pedido: { mensagem: string, tentarDeNovo: () => void, desistir: () => void }) => void)} handler
 */
export function configurarAvisoConcorrencia(handler) {
  onConcorrencia = typeof handler === 'function' ? handler : null;
}

/**
 * Registra quem mostra o atalho "Completar cadastro" quando o back recusa uma movimentação com 422 CADASTRO_INCOMPLETO
 * (cadastro importado do sistema anterior sem dado obrigatório — 4clinics_backend/docs/importacao-planilhas.md, I7).
 * Só avisa: o erro segue para a tela que chamou, que mostra a mensagem como sempre.
 * @param {null | ((recusa: { mensagem: string, nome?: string, campos?: Array<{ campo: string, rotulo: string }>, rota?: string }) => void)} handler
 */
export function configurarAvisoCadastroIncompleto(handler) {
  onCadastroIncompleto = typeof handler === 'function' ? handler : null;
}

export const ehCadastroIncompleto = (erro) =>
  erro?.response?.status === 422 && erro?.response?.data?.codigo === 'CADASTRO_INCOMPLETO';

/** 409 de gravação concorrente: outra pessoa está gravando o mesmo registro agora (o back já repetiu 2 vezes). */
export const ehConcorrencia = (erro) =>
  erro?.response?.status === 409 && erro?.response?.data?.codigo === 'CONCORRENCIA_TENTE_NOVAMENTE';

/**
 * Registra handlers de resposta HTTP do interceptor.
 * @param {{ onUnauthorized?: Function, onForbidden?: Function }} handlers
 */
export function configurarHandlersAuthApi(handlers = {}) {
  onUnauthorized = handlers.onUnauthorized || null;
  onForbidden = handlers.onForbidden || null;
}

api.interceptors.request.use(
  (config) => {
    const metodo = String(config.method || 'get').toLowerCase();
    if (sessaoEsperada) {
      config.headers = config.headers || {};
      Object.assign(config.headers, cabecalhoSessaoEsperada());
    }
    if (csrfToken && !METODOS_SEGUROS.includes(metodo)) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    return config;
  },
  (erro) => Promise.reject(erro)
);

api.interceptors.response.use(
  (resposta) => resposta,
  (erro) => {
    const status = erro?.response?.status;
    // Login com senha errada também é 401: não é "sessão expirada"
    const ehLogin = String(erro?.config?.url || '').includes('/api/auth/login');

    if (status === 401 && !ehLogin) {
      limparSessaoAuth();
      definirCsrfToken(null);
      if (typeof onUnauthorized === 'function') {
        onUnauthorized(erro);
      } else if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }

    // Outra aba entrou com outro usuário/empresa/filial: a aba fica bloqueada até recarregar (sem aviso de 403 a mais)
    if (ehSessaoTrocada(erro)) {
      notificarSessaoTrocada({});
      return Promise.reject(erro);
    }

    if (status === 403) {
      if (typeof onForbidden === 'function') {
        onForbidden(erro);
      }
      // 403 ≠ 401: não desloga automaticamente
    }

    // P5: segura a resposta e pergunta à pessoa; "Tentar de novo" reenvia o mesmo pedido (de novo pelo interceptor)
    if (ehConcorrencia(erro) && typeof onConcorrencia === 'function' && erro.config) {
      return new Promise((resolve, reject) => {
        onConcorrencia({
          mensagem: erro.response.data.message || 'Outra pessoa está gravando este registro agora. Tente de novo.',
          tentarDeNovo: () => api.request(erro.config).then(resolve, reject),
          desistir: () => reject(erro),
        });
      });
    }

    if (ehCadastroIncompleto(erro) && typeof onCadastroIncompleto === 'function') {
      const dados = erro.response.data || {};
      onCadastroIncompleto({ mensagem: dados.message || 'O cadastro do paciente está incompleto.', ...(dados.data || {}) });
    }

    return Promise.reject(erro);
  }
);

export default api;
